import { useQuery } from "@apollo/client";
import PropTypes from "prop-types";
import { createContext, useCallback, useMemo, useState } from "react";
import { matchKeywordGroupsPackageReceipts } from "../graphql/queries/listKeywordGroups";
import { removeEmptyValues } from "../utils/commons";

export const KeywordGroupProductSelectionContext = createContext({});

export const KeywordGroupProductSelectionProvider = ({ children }) => {
  const initialFiltersData = useMemo(
    () => ({
      keyword: "",
      keywordGroupIds: [],
    }),
    []
  );
  const [selectedItems, setSelectedItems] = useState([]);
  const [previousSelected, setPreviousSelected] = useState([]);
  const [filters, setFilters] = useState(initialFiltersData);

  const cleanFilters = useMemo(() => removeEmptyValues(filters), [filters]);

  const { data: keywordGroupsData, loading } = useQuery(
    matchKeywordGroupsPackageReceipts,
    {
      variables: {
        type: "PRODUCT",
        filters: cleanFilters,
      },
    }
  );
  const availableItems = useMemo(() => {
    if (!keywordGroupsData) return [];
    const listAvailableIds = keywordGroupsData?.listKeywordGroups
      ?.filter((product) => {
        if (filters.keyword) return true;
        return !selectedItems.includes(product.id);
      })
      .map((product) => product.id);
    return listAvailableIds;
  }, [keywordGroupsData, selectedItems, filters?.keyword]);

  const toggleItem = useCallback(
    (ids) => {
      if (typeof ids === "object" && !Array.isArray(ids)) return;

      const newProductSet = ids;

      const itemsToDeselect = previousSelected.filter(
        (itemID) => !newProductSet.includes(itemID)
      );

      setSelectedItems((prevSelected) => {
        // Add new items if they are not present.
        const updatedSelection = newProductSet.reduce(
          (acc, id) => {
            if (!acc.includes(id)) {
              acc.push(id);
            }
            return acc;
          },
          [...prevSelected]
        );
        // Remove unchecked items.
        return updatedSelection.filter((item) => !itemsToDeselect.includes(item));
      });
    },
    [previousSelected]
  );

  const handleRemovedData = useCallback(
    (removedData) => {
      const newGloballySelected = selectedItems.filter(
        (item) => !removedData.includes(item)
      );
      setSelectedItems(newGloballySelected);
    },
    [selectedItems]
  );

  const contextValues = useMemo(
    () => ({
      loading,
      keywordGroups: keywordGroupsData ? keywordGroupsData?.listKeywordGroups : [],
      selectedItems,
      setSelectedItems,
      availableItems,
      previousSelected,
      initialFiltersData,
      filters,
      setFilters,
      setPreviousSelected,
      toggleItem,
      handleRemovedData,
    }),
    [
      loading,
      keywordGroupsData,
      selectedItems,
      availableItems,
      previousSelected,
      filters,
      initialFiltersData,
      toggleItem,
      handleRemovedData,
    ]
  );

  return (
    <KeywordGroupProductSelectionContext.Provider value={contextValues}>
      {children}
    </KeywordGroupProductSelectionContext.Provider>
  );
};

KeywordGroupProductSelectionProvider.propTypes = {
  children: PropTypes.node,
};
